import { Action } from '@ngrx/store';

export interface LoopBackFilter {
  limit?: number;
  skip?: number;
  where?: Object;
}

export interface PayloadAction extends Action {
  type: string;
  payload: any;
}

export interface PageEvent {
  pageIndex: number;
  pageSize: number;
  length: number;
}

export class CareAppListActionFactory<T> {
  get REQUEST_ALL() {
    return `[${this.type}][Request] All`;
  }

  get REQUEST_BY_ID() {
    return `[${this.type}][Request] By ID`;
  }

  get SELECT_BY_ID() {
    return `[${this.type}][Select] By ID`;
  }

  get RESPONSE_SUCCESS_ALL() {
    return `[${this.type}][Response][Success] All`;
  }

  get RESPONSE_FAIL_ALL() {
    return `[${this.type}][Response][Fail] All`;
  }

  get RESPONSE_SUCCESS_BY_ID() {
    return `[${this.type}][Response][Success] By ID`;
  }

  get RESPONSE_FAIL_BY_ID() {
    return `[${this.type}][Response][Fail] By ID`;
  }

  get RESPONSE_FAIL() {
    return `[${this.type}][Response][Fail] Generic`;
  }

  get REQUEST_CREATE() {
    return `[${this.type}][Request] Create`;
  }

  get RESPONSE_SUCCESS_CREATE() {
    return `[${this.type}][Response][Success] Create`;
  }

  get RESPONSE_FAIL_CREATE() {
    return `[${this.type}][Response][Fail] Create`;
  }

  get REQUEST_SAVE() {
    return `[${this.type}][Request] Save`;
  }

  get RESPONSE_SUCCESS_SAVE() {
    return `[${this.type}][Response][Success] Save`;
  }

  get RESPONSE_FAIL_SAVE() {
    return `[${this.type}][Response][Fail] Save`;
  }

  get PAGE_CHANGE() {
    return `[${this.type}][Request] Paginate`;
  }

  get PAGE_CHANGE_BY_ID() {
    return `[${this.type}][Request] Paginate (by Id)`;
  }

  get UPDATE_FILTER() {
    return `[${this.type}][Request] Update Filter`;
  }

  get UPDATE_FILTER_BY_ID() {
    return `[${this.type}][Request] Update Filter (by paginator Id)`;
  }

  get RESET_PAGE() {
    return `[${this.type}][Request] Reset Pagination`;
  }

  get REQUEST_PAGE() {
    return `[${this.type}][Request] Update Page`;
  }

  get REQUEST_COUNT() {
    return `[${this.type}][Request] Count`;
  }

  get RESPONSE_SUCCESS_COUNT() {
    return `[${this.type}][Response][Success] Count`;
  }

  get RESPONSE_FAIL_COUNT() {
    return `[${this.type}][Response][Fail] Count`;
  }

  get REQUEST_SEARCH() {
    return `[${this.type}][Request] Search`;
  }

  get RESPONSE_SUCCESS_SEARCH() {
    return `[${this.type}][Response][Success] Search`;
  }

  get RESPONSE_FAIL_SEARCH() {
    return `[${this.type}][Response][Fail] Search`;
  }

  get REQUEST_DESTROY_BY_ID() {
    return `[${this.type}][Request] Destroy By ID`;
  }

  get RESPONSE_SUCCESS_DESTROY_BY_ID() {
    return `[${this.type}][Response][Success] Destroy By ID`;
  }

  get RESPONSE_FAIL_DESTROY_BY_ID() {
    return `[${this.type}][Response][Fail] Destroy By ID`;
  }

  get CLEAR_PAGINATION() {
    return `[${this.type}] Clear Pagination`;
  }

  get CLEAR_PAGINATION_IDS() {
    return `[${this.type}] Clear ID list from pagination entry`;
  }

  get REQUEST_ALL_PAGINATED() {
    return `[${this.type}][Request] All Paginated`;
  }

  get RESPONSE_SUCCESS_ALL_PAGINATED() {
    return `[${this.type}][Response][Success] All Paginated`;
  }

  get RESPONSE_FAIL_ALL_PAGINATED() {
    return `[${this.type}][Response][Fail] All Paginated`;
  }

  get RESPONSE_SUCCESS_BY_ID_CACHED() {
    return `[${this.type}][Response][Success] By ID Cached`;
  }

  constructor(public type: string) {}

  RequestAllAction(payload: { skipPagination?: boolean } = {}) {
    return {
      type: this.REQUEST_ALL,
      payload,
    };
  }

  RequestByIdAction(payload: { id: string; forceRefresh?: boolean }): PayloadAction {
    if (typeof payload !== 'object') {
      throw new Error('Request by id sction requires object payload');
    }
    return {
      type: this.REQUEST_BY_ID,
      payload,
    };
  }

  SelectByIdAction(payload: { id: string }): PayloadAction {
    if (typeof payload !== 'object') {
      throw new Error('Select by id action requires object payload');
    }
    return {
      type: this.SELECT_BY_ID,
      payload,
    };
  }

  ResponseSuccessAllAction(payload: T[]): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_ALL,
      payload,
    };
  }

  ResponseSuccessByIdAction(payload: T): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_BY_ID,
      payload,
    };
  }

  ResponseFailByIdAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_BY_ID,
      payload,
    };
  }

  ResponseFailAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL,
      payload,
    };
  }

  ResponseFailAllAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_ALL,
      payload,
    };
  }

  RequestCreateAction(payload: Partial<T>): PayloadAction {
    return {
      type: this.REQUEST_CREATE,
      payload,
    };
  }

  ResponseSuccessCreateAction(payload: T): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_CREATE,
      payload,
    };
  }

  ResponseFailCreateAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_CREATE,
      payload,
    };
  }

  RequestSaveAction(payload: { id: string; data: Partial<T> }): PayloadAction {
    if (!(<any>payload).id) {
      throw new Error('Save action payload must have an id');
    }
    return {
      type: this.REQUEST_SAVE,
      payload,
    };
  }

  ResponseSuccessSaveAction(payload: T): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_SAVE,
      payload,
    };
  }

  ResponseFailSaveAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_SAVE,
      payload,
    };
  }

  PageChangeAction(payload: PageEvent): PayloadAction {
    return {
      type: this.PAGE_CHANGE,
      payload,
    };
  }

  PageChangeByIdAction(payload: { page: PageEvent; paginatorId: string }): PayloadAction {
    return {
      type: this.PAGE_CHANGE_BY_ID,
      payload,
    };
  }

  ResetPageAction(): Action {
    return {
      type: this.RESET_PAGE,
    };
  }

  RequestPageAction(): Action {
    return {
      type: this.REQUEST_PAGE,
    };
  }

  UpdateFilterByIdAction(payload: any & { paginatorId: string }): PayloadAction {
    return {
      type: this.UPDATE_FILTER_BY_ID,
      payload,
    };
  }

  UpdateFilterAction(payload: any): PayloadAction {
    return {
      type: this.UPDATE_FILTER,
      payload,
    };
  }

  RequestCountAction(): Action {
    return {
      type: this.REQUEST_COUNT,
    };
  }

  ResponseSuccessCountAction(payload: number): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_COUNT,
      payload,
    };
  }

  ResponseFailCountAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_COUNT,
      payload,
    };
  }

  RequestSearchAction(payload: any): PayloadAction {
    return {
      type: this.REQUEST_SEARCH,
      payload,
    };
  }

  ResponseSuccessSearchAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_SEARCH,
      payload,
    };
  }

  ResponseFailSearchAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_SEARCH,
      payload,
    };
  }

  RequestDestroyByIdAction(payload: any): PayloadAction {
    return {
      type: this.REQUEST_DESTROY_BY_ID,
      payload,
    };
  }

  ResponseSuccessDestroyByIdAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_DESTROY_BY_ID,
      payload,
    };
  }

  ResponseFailDestroyByIdAction(payload?: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_DESTROY_BY_ID,
      payload,
    };
  }

  RequestAllPaginatedAction(payload: LoopBackFilter, paginator: string, clear?: boolean) {
    return {
      type: this.REQUEST_ALL_PAGINATED,
      payload,
      clear,
      paginator,
    };
  }

  ResponseSuccessAllPaginatedAction(payload: {
    filter?: LoopBackFilter;
    paginator: string;
    items: T[];
  }): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_ALL_PAGINATED,
      payload,
    };
  }

  ResponseFailAllPaginatedAction(payload: any, paginator: string) {
    return {
      type: this.RESPONSE_FAIL_ALL_PAGINATED,
      paginator,
      payload,
    };
  }

  ClearPaginationAction(paginator: string) {
    return {
      type: this.CLEAR_PAGINATION,
      paginator,
    };
  }

  ClearPaginationIdsAction(paginator: string) {
    return {
      type: this.CLEAR_PAGINATION_IDS,
      paginator,
    };
  }

  ResponseSuccessByIdActionCached(payload: T): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_BY_ID_CACHED,
      payload,
    };
  }

  RequestDestroyAction(payload: any): PayloadAction {
    return {
      type: this.REQUEST_DESTROY_BY_ID,
      payload,
    };
  }

  ResponseSuccessDestroyAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_SUCCESS_DESTROY_BY_ID,
      payload,
    };
  }

  ResponseFailDestroyAction(payload: any): PayloadAction {
    return {
      type: this.RESPONSE_FAIL_DESTROY_BY_ID,
      payload,
    };
  }
}
